/**
 * Filing a folder per person: "Mario Rossi/carta identità.pdf, codice fiscale.pdf,
 * contratto.pdf" goes into Mario Rossi's documents, each file under its item.
 *
 * This is the guessing — whose folder, which document each file is, any date in its
 * name. Pure, so it is tested without a browser. Every guess is shown and can be changed
 * before anything is uploaded (FolderUploadDialog); a wrong guess costs a click, a
 * missing guess costs a choice, and nothing is filed without the office seeing it.
 */

/** Lower case, no accents, words separated by single spaces: "Carta d'Identità" → "carta d identita". */
export function words(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * A file's name without its extension(s): "ci fronte.pdf.p7m" → "ci fronte". An
 * extension starts with a letter, so the year of "scad 31.12.2026.pdf" stays.
 */
export function baseName(filename: string): string {
  return filename.replace(/(\.[a-z][a-z0-9]{0,4}){1,2}$/i, '');
}

// ---------------------------------------------------------------------------
// Whose folder
// ---------------------------------------------------------------------------

export interface Person {
  id: string;
  firstName: string;
  lastName: string;
}

/**
 * The person a folder is named after: "Mario Rossi", "ROSSI MARIO", "rossi_mario",
 * "Rossi Mario - documenti". Null when nobody fits or two people fit equally well, so
 * the office picks rather than the portal guessing between namesakes.
 */
export function matchPerson<P extends Person>(folder: string, people: P[]): P | null {
  const folderWords = new Set(words(folder).split(' ').filter(Boolean));
  if (folderWords.size === 0) return null;

  let best: P | null = null;
  let bestScore = 0;
  let tie = false;
  for (const p of people) {
    const name = words(`${p.firstName} ${p.lastName}`).split(' ').filter(Boolean);
    if (name.length === 0) continue;
    const found = name.filter((w) => folderWords.has(w)).length;
    // Every word of the name in the folder's name: that is them, whatever else it says.
    // Otherwise the surname alone, which is how an office often names a folder.
    const surname = words(p.lastName).split(' ').filter(Boolean);
    const score =
      found === name.length
        ? 2 + (found === folderWords.size ? 1 : 0)
        : surname.length > 0 && surname.every((w) => folderWords.has(w))
          ? 1
          : 0;
    if (score > bestScore) {
      best = p;
      bestScore = score;
      tie = false;
    } else if (score === bestScore && score > 0) {
      tie = true;
    }
  }
  return bestScore > 0 && !tie ? best : null;
}

// ---------------------------------------------------------------------------
// Which document
// ---------------------------------------------------------------------------

export interface DocType {
  code: string;
  label_it: string;
  short_code: string | null;
}

/**
 * What files are called in practice, for the standard items. The item's own name and
 * its file-name code are always tried too, so a folder the office adds ("Nomine") is
 * recognised without being listed here.
 */
const ALSO_CALLED: Record<string, string[]> = {
  id_card: ['carta identita', 'carta di identita', 'carta d identita', 'documento identita', 'documento di identita', 'ci', 'cie', 'identity card', 'id card'],
  tax_code: ['codice fiscale', 'cf', 'tessera sanitaria', 'tesserino fiscale', 'tax code'],
  employment_contract: ['contratto', 'contratto di lavoro', 'contratto lavoro', 'unilav', 'assunzione', 'contract'],
  medical_fitness: ['idoneita', 'idoneita sanitaria', 'idoneita alla mansione', 'visita medica', 'giudizio di idoneita', 'medico competente'],
  training_general: ['formazione generale', 'corso generale'],
  training_specific: ['formazione specifica', 'corso specifico', 'rischio alto', 'rischio medio', 'rischio basso'],
  training_first_aid: ['primo soccorso', 'pronto soccorso'],
  training_fire: ['antincendio', 'addetto antincendio'],
  training_height: ['lavori in quota', 'quota', 'anticaduta', 'dpi terza categoria', 'dpi iii categoria'],
  licence_ple: ['ple', 'piattaforme elevabili', 'piattaforma'],
  licence_pes_pav: ['pes', 'pav', 'pes pav', 'pei', 'cei 11 27', 'rischio elettrico'],
  ppe_record: ['consegna dpi', 'dpi', 'verbale dpi'],
  site_badge: ['badge', 'tesserino', 'tesserino di cantiere', 'badge cantiere'],
};

/** A phrase as whole words inside a name: "ci" in "ci fronte" but not in "certificato". */
const hasPhrase = (haystack: string, phrase: string) => phrase !== '' && ` ${haystack} `.includes(` ${phrase} `);

/**
 * The item a file belongs to, from its name. The longest name that fits wins, so
 * "formazione specifica" beats "formazione", and "dpi terza categoria" beats "dpi".
 */
export function matchType<T extends DocType>(filename: string, types: T[]): T | null {
  const name = words(baseName(filename));
  let best: T | null = null;
  let bestLength = 0;
  for (const t of types) {
    const phrases = [t.label_it, t.short_code ?? '', ...(ALSO_CALLED[t.code] ?? [])].map(words).filter(Boolean);
    for (const phrase of phrases) {
      if (hasPhrase(name, phrase) && phrase.length > bestLength) {
        best = t;
        bestLength = phrase.length;
      }
    }
  }
  return best;
}

/**
 * A picture of the person, for their profile and identification card: an image named
 * "foto", "fototessera", "photo"… and not a scan of a document ("foto carta identità").
 */
/** A picture, by its type or, when the browser gives none, its extension. */
export function isImageFile(file: { name: string; type: string }): boolean {
  return file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic)$/i.test(file.name);
}

export function isPhoto(file: { name: string; type: string }, matchedType: boolean): boolean {
  const name = words(baseName(file.name));
  return isImageFile(file) && !matchedType && ['foto', 'fototessera', 'photo', 'ritratto', 'selfie'].some((w) => hasPhrase(name, w));
}

// ---------------------------------------------------------------------------
// Dates and sides
// ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');

function validDate(y: number, m: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  if (y < 2000 || y > 2100) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

/**
 * An expiry date written in a file's name: "C.I._Mario_Rossi_scadenza_20-05-2031.pdf",
 * "idoneità scad 31.12.2026.pdf", "durc 2026-11-15.pdf". The one after "scad…" when
 * there are several; otherwise the latest, as an issue date comes before an expiry.
 */
export function dateInName(filename: string): string | null {
  const name = baseName(filename).toLowerCase();
  const found: Array<{ at: number; date: string }> = [];
  for (const m of name.matchAll(/(\d{1,2})[-_./ ](\d{1,2})[-_./ ](\d{4})/g)) {
    const date = validDate(Number(m[3]), Number(m[2]), Number(m[1]));
    if (date) found.push({ at: m.index ?? 0, date });
  }
  for (const m of name.matchAll(/(\d{4})[-_./](\d{1,2})[-_./](\d{1,2})/g)) {
    const date = validDate(Number(m[1]), Number(m[2]), Number(m[3]));
    if (date) found.push({ at: m.index ?? 0, date });
  }
  if (found.length === 0) return null;
  const scad = name.indexOf('scad');
  if (scad >= 0) {
    const after = found.filter((f) => f.at > scad).sort((a, b) => a.at - b.at)[0];
    if (after) return after.date;
  }
  return found.map((f) => f.date).sort().at(-1)!;
}

/** Front before back: the first file of a document is the one shown first. */
export function sideOrder(filename: string): number {
  const name = words(baseName(filename));
  if (/\b(fronte|front|davanti|1)\b/.test(name)) return 0;
  if (/\b(retro|back|dietro|2)\b/.test(name)) return 2;
  return 1;
}

// ---------------------------------------------------------------------------
// Reading dropped folders
// ---------------------------------------------------------------------------

/** One folder's files, named after the folder they were in. */
export interface DroppedFolder {
  name: string;
  files: File[];
}

/**
 * Files grouped by the folder holding them, from a folder picker (each File carries
 * "Parent/Mario Rossi/ci.pdf" in webkitRelativePath). Loose files go under "".
 */
export function groupByFolder(files: File[]): DroppedFolder[] {
  const groups = new Map<string, File[]>();
  for (const f of files) {
    const path = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
    const parts = path.split('/');
    const folder = parts.length > 1 ? parts[parts.length - 2]! : '';
    groups.set(folder, [...(groups.get(folder) ?? []), f]);
  }
  return [...groups].map(([name, list]) => ({ name, files: list }));
}

type Entry = FileSystemEntry;

const readAll = (dir: FileSystemDirectoryEntry): Promise<Entry[]> =>
  new Promise((resolve, reject) => {
    const reader = dir.createReader();
    const all: Entry[] = [];
    // Chrome hands back at most 100 entries per call: read until an empty batch.
    const next = () =>
      reader.readEntries((batch) => {
        if (batch.length === 0) resolve(all);
        else {
          all.push(...batch);
          next();
        }
      }, reject);
    next();
  });

const fileOf = (entry: FileSystemFileEntry): Promise<File> => new Promise((resolve, reject) => entry.file(resolve, reject));

/**
 * What was dropped, as folders of files. A dropped folder with files in it is one
 * person; a dropped folder of folders (everybody's at once) is one person per folder
 * inside it. Loose files dropped alongside go into a group with no name.
 */
export async function readDrop(items: DataTransferItemList): Promise<DroppedFolder[]> {
  // The entries must be taken before any await: the list empties once the event ends.
  const entries = [...items].map((i) => i.webkitGetAsEntry?.()).filter((e): e is Entry => Boolean(e));
  const out: DroppedFolder[] = [];
  const loose: File[] = [];

  const collect = async (dir: FileSystemDirectoryEntry, depth: number) => {
    const children = await readAll(dir);
    const files = await Promise.all(children.filter((c) => c.isFile && !c.name.startsWith('.')).map((c) => fileOf(c as FileSystemFileEntry)));
    if (files.length > 0) out.push({ name: dir.name, files });
    // One level down: a folder holding everybody's folders.
    if (depth === 0) {
      for (const child of children.filter((c) => c.isDirectory)) await collect(child as FileSystemDirectoryEntry, 1);
    }
  };

  for (const entry of entries) {
    if (entry.isDirectory) await collect(entry as FileSystemDirectoryEntry, 0);
    else if (!entry.name.startsWith('.')) loose.push(await fileOf(entry as FileSystemFileEntry));
  }
  if (loose.length > 0) out.push({ name: '', files: loose });
  return out;
}
